// A skill's files: the tree on the side, the selected file in a code editor (with a preview for
// Markdown), rename and delete, and Save, which replaces the whole set (`PUT …/files`).
// `SKILL.md` can be neither renamed nor deleted.
import { Eye, FloppyDisk, PencilSimple, Trash } from "@phosphor-icons/react";
import { useState } from "react";
import { ActionIcon, Button, Input } from "rizzui";

import { CodeEditor } from "../catalog/CodeEditor";
import { formatNumber, useT } from "../i18n";
import { cardClass } from "../ui/Card";
import { cn } from "../ui/cn";
import { Hint } from "../ui/SideDrawer";
import { Pill } from "../ui/status";
import type { EditableFile } from "./api";
import { FileTree } from "./FileTree";
import { addFile, byteSize, deleteFile, isMarkdown, MAX_FILE_BYTES, renameFile, SKILL_MD, setContent } from "./files";
import { MarkdownPreview } from "./MarkdownPreview";

interface Props {
  initial: EditableFile[];
  readOnly: boolean;
  onSave: (files: EditableFile[]) => Promise<void>;
}

const kb = (bytes: number) => formatNumber(bytes / 1024, { style: "unit", unit: "kilobyte", maximumFractionDigits: bytes < 10240 ? 1 : 0 });

export function FilesEditor({ initial, readOnly, onSave }: Props) {
  const t = useT().skills.files;
  const [files, setFiles] = useState(initial);
  const [selected, setSelected] = useState(SKILL_MD);
  const [preview, setPreview] = useState(false);
  const [rename, setRename] = useState<{ to: string; error?: string }>();
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const file = files.find((f) => f.path === selected) ?? files[0];
  const size = file ? byteSize(file.content) : 0;
  const tooBig = files.some((f) => byteSize(f.content) > MAX_FILE_BYTES);

  const change = (next: EditableFile[]) => {
    setFiles(next);
    setDirty(true);
  };

  function commitRename() {
    if (!file || !rename) return;
    const result = renameFile(files, file.path, rename.to.trim());
    if (!result.ok) return setRename({ ...rename, error: result.error });
    change(result.files);
    setSelected(rename.to.trim());
    setRename(undefined);
  }

  async function save() {
    setSaving(true);
    setError(undefined);
    try {
      await onSave(files);
      setDirty(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className={cn(cardClass, "grid gap-0 lg:grid-cols-[260px_1fr]")} aria-label={t.label}>
      <div className="border-b border-muted p-4 lg:border-b-0 lg:border-e">
        <h3 className="mb-3 text-sm font-semibold text-gray-900">{t.title}</h3>
        <FileTree
          files={files}
          selected={file?.path ?? ""}
          onSelect={(p) => {
            setSelected(p);
            setRename(undefined);
          }}
          onAdd={(path) => {
            if (readOnly) return t.readOnly;
            const result = addFile(files, path);
            if (!result.ok) return result.error;
            change(result.files);
            setSelected(path);
            setPreview(false);
            return undefined;
          }}
        />
      </div>
      <div className="min-w-0 space-y-3 p-4">
        {file ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {rename ? (
                <Input
                  className="min-w-0 flex-1"
                  size="sm"
                  aria-label={t.renameTo}
                  autoFocus
                  inputClassName="font-mono"
                  value={rename.to}
                  error={rename.error}
                  onChange={(e) => setRename({ to: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitRename();
                    if (e.key === "Escape") setRename(undefined);
                  }}
                  onBlur={commitRename}
                />
              ) : (
                <span className="min-w-0 flex-1 truncate font-mono text-[13px] font-semibold text-gray-900">{file.path}</span>
              )}
              <Pill tone={size > MAX_FILE_BYTES ? "red" : "gray"}>{kb(size)}</Pill>
              {isMarkdown(file.path) ? (
                <Button size="sm" variant="outline" className="gap-1.5" aria-pressed={preview} onClick={() => setPreview((p) => !p)}>
                  {preview ? <PencilSimple className="h-4 w-4" /> : <Eye className="h-4 w-4" />} {preview ? t.edit : t.preview}
                </Button>
              ) : null}
              {file.path !== SKILL_MD && !readOnly ? (
                <>
                  <ActionIcon size="sm" variant="outline" aria-label={t.rename(file.path)} onClick={() => setRename({ to: file.path })}>
                    <PencilSimple className="h-4 w-4" />
                  </ActionIcon>
                  <ActionIcon
                    size="sm"
                    variant="outline"
                    aria-label={t.delete(file.path)}
                    className="hover:border-red hover:text-red"
                    onClick={() => {
                      const result = deleteFile(files, file.path);
                      if (result.ok) {
                        change(result.files);
                        setSelected(SKILL_MD);
                      }
                    }}
                  >
                    <Trash className="h-4 w-4" />
                  </ActionIcon>
                </>
              ) : null}
            </div>
            {preview && isMarkdown(file.path) ? (
              <div className="min-h-[18rem] rounded-lg border border-muted p-4">
                <MarkdownPreview source={file.content} />
              </div>
            ) : (
              <CodeEditor label={t.content(file.path)} rows={20} value={file.content} onChange={(content) => !readOnly && change(setContent(files, file.path, content))} />
            )}
            {file.path === SKILL_MD ? <Hint>{t.skillMdHint}</Hint> : null}
          </>
        ) : null}
        {error ? <p className="text-[13px] text-red-dark">{error}</p> : null}
        {readOnly ? null : (
          <div className="flex items-center justify-end gap-3 border-t border-muted pt-3">
            {tooBig ? <span className="text-[13px] text-red-dark">{t.tooBig}</span> : null}
            <Button className="gap-1.5" isLoading={saving} disabled={!dirty || tooBig} onClick={() => void save()}>
              <FloppyDisk className="h-4 w-4" /> {dirty ? t.save : t.saved}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
